import { Table } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { alphaToggleAtom } from '../../../atoms/alpha';
import { secondsToTimeStamp } from '../../../utils/time';
import BooleanDisplay from '../../BooleanDisplay';

const VALUE_TRANSLATIONS = {
  avg_frame_rate_str: {
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Average Frame Rate" id="d099e" />,
  },
  bitrate: {
    displayHandler: (rate) =>
      rate ? (
        <FormattedNumber
          value={(rate / 1000).toFixed(0)}
          // Required as react-intl interface utilises style prop
          // eslint-disable-next-line react/style-prop-object
          style="unit"
          unit="kilobyte"
          unitDisplay="narrow"
        />
      ) : (
        <FormattedMessage defaultMessage="Unknown" id="88183" />
      ),
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Bitrate" id="700a0" />,
  },
  codec: {
    title: <FormattedMessage defaultMessage="Codec" id="8ca99" />,
  },
  codec_long_name: {
    displayHandler: (name) => name.replaceAll(' / ', '/'),
    hidden: true,
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Long Name" id="28549" />,
  },
  codec_profile: {
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Codec Profile" id="a1e96" />,
  },
  duration: {
    displayHandler: (duration) => (
      <FormattedMessage
        defaultMessage="{timestamp}"
        id="da51a"
        values={{
          timestamp: duration ? (
            secondsToTimeStamp(duration)
          ) : (
            <FormattedMessage defaultMessage="Unknown" id="88183" />
          ),
        }}
      />
    ),
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Duration" id="e02d2" />,
  },
  frame_rate: {
    title: <FormattedMessage defaultMessage="Frame Rate" id="9cb33" />,
  },
  has_b_frames: {
    displayHandler: (hasBFrames) => <BooleanDisplay value={hasBFrames} />,
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Has B Frames" id="7fa4e" />,
  },
  is_fixed_frame_rate: {
    displayHandler: (isFixed) => <BooleanDisplay value={isFixed} />,
    title: <FormattedMessage defaultMessage="Is Fixed Frame Rate" id="b63f3" />,
  },
  number_of_frames: {
    displayHandler: (frames) =>
      frames || <FormattedMessage defaultMessage="Unknown" id="88183" />,
    shouldRespectAlphaToggle: true,
    title: <FormattedMessage defaultMessage="Number of Frames" id="c9611" />,
  },
  resolution: {
    displayHandler: (resolution) => (
      <FormattedMessage
        defaultMessage="{width}x{height}"
        id="956cb"
        values={{ ...resolution }}
      />
    ),
    title: <FormattedMessage defaultMessage="Resolution" id="b5a4b" />,
  },
};

export function MetadataValue({ dataTypeIdentifier, value }) {
  const isAlphaEnabled = useAtomValue(alphaToggleAtom);
  return (!VALUE_TRANSLATIONS[dataTypeIdentifier]?.shouldRespectAlphaToggle ||
    (VALUE_TRANSLATIONS[dataTypeIdentifier]?.shouldRespectAlphaToggle &&
      isAlphaEnabled)) &&
    !VALUE_TRANSLATIONS[dataTypeIdentifier]?.hidden ? (
    <Table.Row>
      <Table.DataCell $textAlign="left">
        {VALUE_TRANSLATIONS[dataTypeIdentifier]?.title || dataTypeIdentifier}
      </Table.DataCell>

      <Table.DataCell $textAlign="left">
        {(VALUE_TRANSLATIONS[dataTypeIdentifier]?.displayHandler &&
          VALUE_TRANSLATIONS[dataTypeIdentifier].displayHandler(value)) ||
          value.toString()}
      </Table.DataCell>
    </Table.Row>
  ) : null;
}

MetadataValue.propTypes = {
  dataTypeIdentifier: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default { MetadataValue };
